import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import {
  Warning,
  PendingActions,
  CheckCircle,
  RemoveShoppingCart,
  Today,
} from '@mui/icons-material';
import { api } from '../services/api';

interface DashboardStats {
  pendingOrders: number;
  partialFulfilledOrders: number;
  lowStockItems: number;
  outOfStockItems: number;
  ordersPlacedToday: number;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    pendingOrders: 0,
    partialFulfilledOrders: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    ordersPlacedToday: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/api/dashboard');
      setStats({
        pendingOrders: response.data.pendingOrders || 0,
        partialFulfilledOrders: response.data.partialFulfilledOrders || 0,
        lowStockItems: response.data.lowStockItems || 0,
        outOfStockItems: response.data.outOfStockItems || 0,
        ordersPlacedToday: response.data.ordersPlacedToday || 0,
      });
    } catch (err) {
      console.error('Stats load error', err);
    } finally {
      setLoading(false);
    }
  };

  // Get date 1 month ago in YYYY-MM-DD format
  const getOneMonthAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  };

  const getTodayString = () => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  };

  const dashboardCards = [
    {
      title: 'Pending Orders',
      subtitle: 'Last 1 month • Customer Outward',
      value: stats.pendingOrders,
      icon: <PendingActions />,
      borderColor: '#1976d2',
      onClick: () => {
        const startDate = getOneMonthAgo();
        const endDate = getTodayString();
        navigate(`/customers/orders?status=PENDING&direction=OUTWARD&start_date=${startDate}&end_date=${endDate}`);
      },
    },
    {
      title: 'Partial Fulfilled Orders',
      subtitle: 'Last 1 month • Customer Outward',
      value: stats.partialFulfilledOrders,
      icon: <CheckCircle />,
      borderColor: '#ed6c02',
      onClick: () => {
        const startDate = getOneMonthAgo();
        const endDate = getTodayString();
        navigate(`/customers/orders?status=PARTIAL_FULFILLED&direction=OUTWARD&start_date=${startDate}&end_date=${endDate}`);
      },
    },
    {
      title: 'Orders Placed Today',
      subtitle: 'Customer Outward orders',
      value: stats.ordersPlacedToday,
      icon: <Today />,
      borderColor: '#2e7d32',
      onClick: () => {
        const today = getTodayString();
        navigate(`/customers/orders?direction=OUTWARD&start_date=${today}&end_date=${today}`);
      },
    },
    {
      title: 'Low Stock Items',
      subtitle: 'Items below minimum stock level',
      value: stats.lowStockItems,
      icon: <Warning />,
      borderColor: '#ff9800',
      valueColor: '#ff9800',
      onClick: () => {
        navigate('/items/stock?status=low_stock');
      },
    },
    {
      title: 'Out of Stock Items',
      subtitle: 'Items with zero stock',
      value: stats.outOfStockItems,
      icon: <RemoveShoppingCart />,
      borderColor: '#d32f2f',
      valueColor: '#d32f2f',
      onClick: () => {
        navigate('/items/stock?status=out_of_stock');
      },
    },
  ];

  return (
    <Box sx={{ p: 3, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Warehouse Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          System health and operational summary
        </Typography>
      </Box>

      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 3,
          flexGrow: 1,
        }}
      >
        {dashboardCards.map((card, index) => (
          <Card
            key={index}
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              borderLeft: `8px solid ${card.borderColor}`,
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              },
            }}
          >
            <CardActionArea
              onClick={card.onClick}
              sx={{ height: '100%', p: 0 }}
            >
              <CardContent sx={{ width: '100%', p: 3, position: 'relative', overflow: 'hidden', minHeight: 140 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    right: 60,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0.15,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {React.cloneElement(card.icon, { sx: { fontSize: 80, color: card.borderColor } })}
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {card.subtitle}
                  </Typography>
                  <Typography variant="h6" fontWeight="500" gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h2"
                    fontWeight="800"
                    sx={{
                      lineHeight: 1,
                      color: card.valueColor || 'inherit',
                    }}
                  >
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default Dashboard;